import React, { useContext } from "react";
import { motion } from "framer-motion";
import { Globe, ChevronRight, Check } from "lucide-react";
import CommonPageTitle from "../../components/CommonPageTitle";
import TranslationProviderContext from "../../context/TranslationProviderContext";
import { SupportedLanguageCode } from "../../types/types";
import useThemeHook from "../../hooks/useThemeHook";
import { THEME_COLORS } from "../../themes/DarkLight";

interface LanguageOption {
  code: SupportedLanguageCode;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: LanguageOption[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt", flag: "🇻🇳" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "ko", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
];

const SettingsPage: React.FC = () => {
  const { appLanguageCode, updateAppLanguageCode } = useContext(
    TranslationProviderContext
  );
  const theme = useThemeHook();

  const handleLanguageChange = async (code: SupportedLanguageCode) => {
    await updateAppLanguageCode(code);
    // Language change is now applied live via i18n.changeLanguage()
  };

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: THEME_COLORS[theme].page_background_color }}
    >
      {/* Header */}
      <div className="px-4 pt-4">
        <CommonPageTitle padding={0} title="Settings" />
      </div>

      {/* Language Section */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-purple-600" />
          <h3
            className="font-semibold"
            style={{ color: THEME_COLORS[theme].inverse_theme }}
          >
            Language
          </h3>
        </div>

        <div
          className="rounded-2xl shadow-sm border overflow-hidden"
          style={{
            backgroundColor: THEME_COLORS[theme].full_theme,
            borderColor: THEME_COLORS[theme].gray + "30",
          }}
        >
          {languages.map((language, index) => (
            <motion.button
              key={language.code}
              whileHover={{ backgroundColor: THEME_COLORS[theme].gray + "10" }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full flex items-center justify-between p-4 transition-colors ${
                index !== languages.length - 1 ? "border-b" : ""
              }`}
              style={{
                borderColor: THEME_COLORS[theme].gray + "20",
                backgroundColor:
                  appLanguageCode === language.code
                    ? THEME_COLORS[theme].gray + "10"
                    : "transparent",
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{language.flag}</span>
                <div className="text-left">
                  <div
                    className="font-medium"
                    style={{ color: THEME_COLORS[theme].inverse_theme }}
                  >
                    {language.nativeName}
                  </div>
                  <div
                    className="text-sm"
                    style={{ color: THEME_COLORS[theme].gray }}
                  >
                    {language.name}
                  </div>
                </div>
              </div>
              {appLanguageCode === language.code ? (
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              ) : (
                <ChevronRight
                  className="w-5 h-5"
                  style={{ color: THEME_COLORS[theme].gray }}
                />
              )}
            </motion.button>
          ))}
        </div>

        <p
          className="mt-3 text-sm px-2"
          style={{ color: THEME_COLORS[theme].gray }}
        >
          Changing the language will reload the app to apply your selection.
        </p>
      </div>

      {/* Bottom spacing for nav */}
      <div className="h-20" />
    </div>
  );
};

export default SettingsPage;
